import type { FiltersQuery, Meta } from '@upbeat/shared';

/**
 * Dashboard filter state. Lists hold the *selected* values; a list equal to the full
 * choice set means "no restriction" and is omitted from both the URL and the API query.
 */
export const AFR_MAX_YEAR = 2023;

export interface FilterState {
  indicator: string;
  from: number;
  to: number;
  mapYear?: number;
  region: string[];
  income: string[];
  ownership: string[];
}

export type Choices = Pick<Meta, 'regions' | 'income_groups' | 'ownerships' | 'years' | 'default_indicator'> & {
  indicatorNames: Set<string>;
};

export function choicesFromMeta(m: Meta): Choices {
  return {
    regions: m.regions,
    income_groups: m.income_groups,
    ownerships: m.ownerships,
    years: m.years,
    default_indicator: m.default_indicator,
    indicatorNames: new Set(m.indicators.flatMap((g) => g.indicators.map((i) => i.indicator))),
  };
}

export function defaultState(c: Choices): FilterState {
  const maxMap = Math.min(c.years.to, AFR_MAX_YEAR);
  return {
    indicator: c.default_indicator,
    from: c.years.from,
    to: c.years.to,
    mapYear: maxMap,
    region: c.regions.map((r) => r.code),
    income: [...c.income_groups],
    ownership: [...c.ownerships],
  };
}

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

function parseList(raw: string | null, allowed: string[]): string[] {
  if (raw === null) return [...allowed];
  const picked = raw.split(',').map((s) => s.trim()).filter((s) => allowed.includes(s));
  return picked.length ? picked : [...allowed];
}

function parseYear(raw: string | null, fallback: number, c: Choices): number {
  const n = raw === null ? NaN : Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(c.years.to, Math.max(c.years.from, n));
}

/** URL search params -> state. Unknown or malformed values fall back to defaults. */
export function parseState(sp: URLSearchParams, c: Choices): FilterState {
  const d = defaultState(c);
  const ind = sp.get('indicator');
  const from = parseYear(sp.get('from'), d.from, c);
  const to = parseYear(sp.get('to'), d.to, c);
  const mapYearRaw = sp.get('map_year');
  const mapYear = mapYearRaw ? parseYear(mapYearRaw, d.mapYear ?? AFR_MAX_YEAR, c) : d.mapYear;
  return {
    indicator: ind && c.indicatorNames.has(ind) ? ind : d.indicator,
    from: Math.min(from, to),
    to: Math.max(from, to),
    mapYear,
    region: parseList(sp.get('region'), d.region),
    income: parseList(sp.get('income'), d.income),
    ownership: parseList(sp.get('ownership'), d.ownership),
  };
}

/** State -> URL search params, omitting anything equal to the default.
 * If baseSp is provided, non-filter query parameters (e.g. 'countries') are preserved.
 */
export function serializeState(s: FilterState, c: Choices, baseSp?: URLSearchParams): URLSearchParams {
  const d = defaultState(c);
  const sp = baseSp ? new URLSearchParams(baseSp) : new URLSearchParams();
  if (baseSp) {
    for (const key of ['indicator', 'from', 'to', 'map_year', 'region', 'income', 'ownership']) {
      sp.delete(key);
    }
  }
  if (s.indicator !== d.indicator) sp.set('indicator', s.indicator);
  if (s.from !== d.from) sp.set('from', String(s.from));
  if (s.to !== d.to) sp.set('to', String(s.to));
  if (s.mapYear !== undefined && s.mapYear !== d.mapYear) sp.set('map_year', String(s.mapYear));
  if (!sameSet(s.region, d.region)) sp.set('region', s.region.join(','));
  if (!sameSet(s.income, d.income)) sp.set('income', s.income.join(','));
  if (!sameSet(s.ownership, d.ownership)) sp.set('ownership', s.ownership.join(','));
  return sp;
}

/** State -> API query. Full lists are omitted so the API applies no restriction. */
export function toQuery(s: FilterState, c: Choices): FiltersQuery {
  const d = defaultState(c);
  const q: FiltersQuery = { indicator: s.indicator, from: s.from, to: s.to };
  if (!sameSet(s.region, d.region)) q.region = s.region.join(',');
  if (!sameSet(s.income, d.income)) q.income = s.income.join(',');
  if (!sameSet(s.ownership, d.ownership)) q.ownership = s.ownership.join(',');
  return q;
}

export const isEmptySelection = (s: FilterState) =>
  s.region.length === 0 || s.income.length === 0 || s.ownership.length === 0;
